import React, { useContext, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import AsyncSelect from "react-select/async";

import ReactModal from "react-modal";
import useArtworkCollection from "routes/master-artwork/hooks/useArtworkCollectionHook";

import useAxios from "../../../../../utils/useAxios";
import AuthContext from "../../../../../context/AuthContext";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../../context/DebounceContext";
import {
  formatCollectionOptions,
  getCollections,
} from "../../../helpers/collections.helper";
import useMetaArtworkNameAndNumberRequests from "../../../hooks/useArtworkNameAndNumberRequestsHook";
import { MasterArtworkNumberActions } from "../../../../../states/meta-artwork/artwork-number";
import Spinner from "../../../../../components/Spinner";
import { Predicates } from "libraries/predicates/predicates";
import OptionFormatter from "../../../../../components/OptionFormatter";
import ExpandableSection from "../../../../skus/components/expandable-section/ExpandableSection";
import ArtworkUploadModal from "../../../components/artwork-upload-modal";
import {
  formatArtworkNameOptions,
  getArtworkName,
} from "../../../helpers/artworkName.helper";
import { useMasterArtworkNumberStore } from "../../../../../context/MasterArtworkNumberContext";
import ArtworkNumberActionButtonsGroup from "routes/master-artwork/components/artwork-number-action-buttons/artworkNumberActionButtons";
import {
  artworkDesignGlobalPermissions,
  artworkMasterDataGlobalPermissions,
} from "../../../helpers/artworkPermissions";
import isReadOnly from "../../../helpers/isReadOnlyHelper";
import { getGlobalProducts } from "../../../../../helpers/global-product.helper";
import Banner from "../../../../../components/Banner";
import { ARTWORK_NUMBER_VALIDATION_RULE } from "../../../helpers/artworkCodesValidationRules";
import CreatableSelect from "react-select/creatable";
import ArtworkPictureModal from "routes/master-artwork/components/ArtworkPicutureModal";
import ArtworkRequestHistoryTable from "../../../components/ArtworkRequestHistoryComponent";
import useArtworkGenericRequests from "../../../hooks/useArtworkGenericReaquestsHook";

export const ArtworkMasterNumberCreatePage = (props: any) => {
  const axios = useAxios();
  const authContext = useContext(AuthContext);
  const username = authContext?.username;
  const { id, code } = useParams();
  const location = useLocation()

  const hasCreatorPermission = artworkDesignGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );
  const hasApproverPermission = artworkMasterDataGlobalPermissions.every(
    (permission) => authContext?.hasPermission(permission),
  );

  const [isSaveOrSubmitInProgress, setIsSaveOrSubmitInProgress] =
    useState<boolean>(false);


  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState<boolean>(() => !!urlSearchParams.get("editing")  && hasApproverPermission);

  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);

  const { state, dispatch } = useMasterArtworkNumberStore();

  const { data: collectionsData, isLoading: collectionsIsLoading } =
    useArtworkCollection({});
  const collectionsOptions = getCollections(collectionsData);

  const { data: artworkNumberData, isLoading: artworkNumberIsLoading } =
    useMetaArtworkNameAndNumberRequests(
      { search: state.collection?.code },
      isSaveOrSubmitInProgress,
    );

  const artworkNameData = Array.from(
    new Set(artworkNumberData.map((artwork) => artwork.data.artwork_name)),
  ).filter(
    (item, index, self) =>
      index ===
      self.findIndex((t) => t.name === item.name && t.code === item.code),
  );

  const artworkNameOptions = getArtworkName(artworkNameData);

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const closeModal = () => {
    setShowPictureModal(false);
  };

  const [artworkNumberImg, setArtworkNumberImg] = useState<any>();
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getArtworkNumberImage = async (fileName: string) => {
    const response = await axios.get(
      `/artwork-master/meta-artwork/number-upload-image/?image_file_name=${fileName}`,
    );
    setArtworkNumberImg(response.data);
  };

  const getArtworkNumberRequest = async (id: string | number) => {
    const response = await axios.get(`/request/${id}`);
    const requestStatus = response.data.status;
    const isReadOnlyValue = isReadOnly(
      response.data.status,
      hasApproverPermission,
    );

    dispatch(
      MasterArtworkNumberActions.setArtworkNumber({
        ...state,
        requestStatus: requestStatus,
        ...response.data.data,
        requestId: response.data.id,
        isReadOnly: isReadOnlyValue,
      }),
    );

    setIsLoading(false);
  };

  const [codeAlreadyExists, setCodeAlreadyExists] = useState<boolean>(false);

  const verifyIArtworkNumberAlreadyExists = async (
    artworkNumberCode: string | number
  ): Promise<void> => {
    try {
      setIsSaveOrSubmitInProgress(true);

      const response = await axios.get(
        `artwork-master/request/?search=${artworkNumberCode}&context=44`
      );

      const hasMatchingRecords = response.data.length > 0;
      if (!hasMatchingRecords) {
        setCodeAlreadyExists(false);
        return;
      }

      const firstRecord = response.data[0];
      const isExistingCode = id
        ? firstRecord.id !== Number(id)
        : state.code !== code;

      setCodeAlreadyExists(isExistingCode);
    } catch (error) {
      console.error('Failed to verify artwork number:', error);
      setCodeAlreadyExists(false);
    } finally {
      setIsSaveOrSubmitInProgress(false);
    }
  };

  useEffect(() => {
    verifyIArtworkNumberAlreadyExists(state.code);
  }, [id, state.code, location]);

  const [historyRecords, setHistoryRecords] = useState<any[]>([]);

  const getRequestHistory = async (id: string | number) => {
    const response = await axios.get(`/request-history/?request__id=${id}`);

    // @ts-ignore
    setHistoryRecords(response.data);
  };

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      getArtworkNumberRequest(id);
      getRequestHistory(id);
      return;
    }

    dispatch(MasterArtworkNumberActions.resetState());
  }, [id, location]);

  useEffect(() => {
    // @ts-ignore
    if (!!state.file_name) {
      // @ts-ignore
      getArtworkNumberImage(state.file_name);
    }
  }, [state.file_name]);

  const handleCreateArtworkName = (
    key: "name" | "code" | "launch_date" | "collection",
    value: any,
  ) => {
    const newArtworkName = { ...state.artwork_name };

    if (key === "code") {
      const codeIndex = artworkNameOptions.findIndex(
        (artwork: any) => artwork.value.code === value,
      );

      if (codeIndex >= 0) {
        dispatch(
          MasterArtworkNumberActions.setArtworkName(
            artworkNameOptions[codeIndex].value,
          ),
        );
        return;
      }
    }

    if (key === "name") {
      newArtworkName["name"] = value;
      newArtworkName["code"] = code ? state.artwork_name.code:"";
      newArtworkName["launch_date"] = code ? state.artwork_name.launch_date:"";
      newArtworkName["collection"] = state.collection;

      dispatch(MasterArtworkNumberActions.setArtworkName(newArtworkName));
      return;
    }

    newArtworkName[key] = value;

    dispatch(MasterArtworkNumberActions.setArtworkName(newArtworkName));
  };

  const setNextArtworkNumber = () => {
    const filteredArtworks = artworkNumberData.filter((artwork) => {
      return artwork.data.code.includes(
        `${state.collection?.code}_${state.artwork_name?.code}_`,
      );
    });

    if(!code) {
      dispatch(
        MasterArtworkNumberActions.setCode(
          `${state.collection?.code}_${state.artwork_name?.code}_${filteredArtworks.length + 1}`,
        ),
      );
    }
  };

  const getMissingConditionsMessage = () => {
    const levelRequired = "full";

    const conditions = [
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state.collection?.name,
          ),
        message: `<strong>Collection</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          Predicates.isNotNullAndNotUndefinedAndNotEmpty(
            state.artwork_name?.name,
          ),
        message: `<strong>Meta Artwork Name</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          ARTWORK_NUMBER_VALIDATION_RULE.test(state.code),
        message: `<strong>Meta Artwork number</strong> field is required`,
        conditionLevel: "full",
      },
      {
        condition: (state: any) =>
          hasApproverPermission ||
          (!codeAlreadyExists &&
            !artworkNumberData.some((item) => item.data.code === state.code)),
        message: `<strong>Meta Artwork number</strong> already taken, try next number`,
        conditionLevel: "full",
      },
      {
        // condition: (state: any) => hasApproverPermission && !!state.code && !!id && codeAlreadyExists ,
        condition: (state: any) => !codeAlreadyExists,
        message: `<strong>Meta Artwork number</strong> already taken and approved, try next number`,
        conditionLevel: "full",
      },
    ];

    const missingConditions = conditions.filter(
      (condition) =>
        (levelRequired === "full"
          ? true
          : condition.conditionLevel === levelRequired) &&
        !condition.condition(state),
    );

    if (missingConditions.length < 1) {
      return "";
    }

    let message =
      "You cannot proceed to collection creation. The following requirements are missing <br> <ul class='mb-0'>";
    message += missingConditions
      .map((condition) => `<li>${condition.message}</li>`)
      .join("");
    return message + "</ul>";
  };

  useEffect(() => {
    const isValid = !!getMissingConditionsMessage() || codeAlreadyExists;

    dispatch(MasterArtworkNumberActions.setIsValid(!isValid));

    if (state.requestStatus === null || state.requestStatus === "") {
      dispatch(MasterArtworkNumberActions.setRequestStatus("CREATED"));
    }
  }, [state.artwork_name, state.collection, codeAlreadyExists, state.code]);

  const [searchParams, setSearchParams] = useState<any>({
    limit: 1,
    lastDays: -1,
    requestor: "",
    context: [44],
    status: ["APPROVED"],
    search: code,
    hasFilterApplyed: false,
    offset: 0,
  });

  const { data: metaArtwork, isLoading: metaArtworkIsLoading } =
    useArtworkGenericRequests({ ...searchParams });

  useEffect(() => {
    if (code) {
      setIsLoading(true);
      //@ts-ignore
      if (metaArtwork) {
        //@ts-ignore
        const dataResult = metaArtwork.results[0];
        const metaArtworkDTO = {
          ...dataResult.data,
        };

        //@ts-ignore
        dispatch(
          MasterArtworkNumberActions.setArtworkNumber({
            ...metaArtworkDTO,
            requestStatus: dataResult.status,
            requestId: dataResult.id,
          }),
        );
        dispatch(MasterArtworkNumberActions.setIsReadOnly(!isEditing));

        setHistoryRecords(metaArtworkDTO.requestHistory);
        getArtworkNumberImage(metaArtworkDTO.file_name);
      }
      setIsLoading(metaArtworkIsLoading);
    }
  }, [metaArtwork]);

  useEffect(() => {
    if(code){
      dispatch(MasterArtworkNumberActions.setIsReadOnly(!isEditing));
    }
  }, [code]);

  return (
    <>
      {isLoading && <Spinner />}
      {!isLoading && (
        <div
          className="container-fluid mt-3 bg-white shadow py-3 rounded-1 d-flex flex-column justify-contnet-between h-100"
          style={{ minHeight: "70vh" }}
        >
          <div className="row h6 text-primary mb-0 bg-gray-100 p-2 rounded-1">
            Meta Artwork Hierarchy
          </div>

          <div className="row mb-0">
            <div className="col-md-6 mb-2 mt-1">
              <label className="form-label">Collection Code/Name*</label>
              <AsyncSelect
                name="collection"
                value={
                  Predicates.isNotNullAndNotUndefined(state.collection)
                    ? formatCollectionOptions(state.collection)
                    : null
                }
                onChange={(e) => {
                  dispatch(
                    MasterArtworkNumberActions.setCollection(e?.value ?? null),
                  );

                  handleCreateArtworkName("collection", e?.value);

                  dispatch(MasterArtworkNumberActions.setArtworkName(null));
                }}
                defaultOptions={collectionsOptions}
                loadOptions={(input, callback) => {
                  searchDebounce(
                    collectionsOptions,
                    input,
                    getCollections(collectionsData),
                    callback,
                  );
                }}
                formatOptionLabel={OptionFormatter}
                isLoading={collectionsIsLoading}
                isClearable
                isSearchable
                placeholder="(min 3 characters)"
                classNamePrefix="react-select"
                isDisabled={state.isReadOnly}
              />
            </div>

            <div className="col-md-2 form-group mt-1">
              <label className="form-label">Collection Launch Date</label>
              <textarea
                rows={1}
                value={state.collection?.launch_date}
                className="form-control d-inline no-resize horizontal-scroll"
                disabled
                key={`${state.collection?.id}_launch_date`}
              />
            </div>
            <div className="col-md-2 form-group mt-1">
              <label className="form-label">Collection Licensor</label>
              <textarea
                rows={1}
                value={state.collection?.licensor?.name}
                className="form-control d-inline no-resize horizontal-scroll"
                disabled
                key={`${state.collection?.id}_licensor`}
              />
            </div>
            <div className="col-md-2 form-group mt-1">
              <label className="form-label">Collection License</label>
              <textarea
                rows={1}
                value={state.collection?.licensed_property?.name}
                className="form-control d-inline no-resize horizontal-scroll"
                disabled
                key={`${state.collection?.id}_licensedProperty`}
              />
            </div>
            <div className="col-md-6 col-lg-auto form-group mt-1 flex-fill">
              <label className="form-label">Global Product Code - Name</label>

              <AsyncSelect
                name="globalProduct"
                value={getGlobalProducts(
                  state.collection?.global_product ?? [],
                )}
                isMulti
                formatOptionLabel={OptionFormatter}
                isLoading={isLoading}
                isClearable
                isSearchable
                classNamePrefix="react-select"
                isDisabled={true}
                key={`${state.collection?.id}_gp`}
              />
            </div>
          </div>

          <ExpandableSection
            className="mb-2 mt-4"
            sectionId="create-new-artwork-name"
            title="Select or Create Artwork Name"
            expandedByDefault={true}
          >
            <div className="row">
              <div className="col-md-6 form-group">
                <label className="form-label">Meta Artwork Name*</label>
                <CreatableSelect
                  name="artwork-name-code"
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      state.artwork_name?.name,
                    )
                      ? formatArtworkNameOptions(state.artwork_name)
                      : null
                  }
                  onChange={(e) => {
                    if (code && isEditing) {
                    } else {
                      dispatch(
                        MasterArtworkNumberActions.setArtworkName(
                          // @ts-ignore
                          e?.value ?? null,
                        ),
                      );
                      if (!state.collection) {
                        dispatch(
                          MasterArtworkNumberActions.setCollection(
                            e?.value?.collection ?? null,
                          ),
                        );
                      }
                    }
                  }}
                  options={artworkNameOptions}
                  onBlur={() => {
                    setNextArtworkNumber();
                    getMissingConditionsMessage();
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={artworkNumberIsLoading}
                  isClearable={true}
                  onCreateOption={(e: any) => {
                    handleCreateArtworkName("name", e);
                    // handleCreateArtworkName("code", "");
                    // handleCreateArtworkName("launch_date", "");
                  }}
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  isDisabled={state.isReadOnly}
                  key={`${state.collection?.id}_artwork_name`}
                />
              </div>

              <div className="col-md-3 form-group">
                <label className="form-label">Meta Artwork Name Code*</label>
                <input
                  type="text"
                  value={state?.artwork_name?.code}
                  onChange={(e: any) => {
                    handleCreateArtworkName(
                      "code",
                      e.target.value.toUpperCase(),
                    );
                  }}
                  onKeyPress={(e) => {
                    const regex = /^[A-Za-z0-9_]$/;
                    if (!regex.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={() => {
                    setNextArtworkNumber();
                    getMissingConditionsMessage();
                  }}
                  placeholder="Max 6 characters (AAA_EN)"
                  maxLength={6}
                  className="form-control d-inline no-resize horizontal-scroll"
                  disabled={state.isReadOnly}
                />
              </div>

              <div className="col-md-3 form-group">
                <label className="form-label">
                  Meta Artwork Name Launch Date
                </label>
                <input
                  onChange={(e: any) => {
                    handleCreateArtworkName("launch_date", e.target.value);
                  }}
                  type="date"
                  // rows={1}
                  value={state.artwork_name?.launch_date?.split(" ")[0]}
                  min={state.collection?.launch_date}
                  className="form-control d-inline no-resize horizontal-scroll"
                  disabled={state.isReadOnly}
                />
              </div>
            </div>
          </ExpandableSection>

          <ExpandableSection
            className="mb-2 mt-4"
            sectionId="create-new-artwork-number"
            title="Create new Artwork Number"
            expandedByDefault={true}
          >
            {/*@ts-ignore*/}
            <div className="row">
              <div className="col-md-2 form-group align-self-center mb-0">
                <label className="form-label">Prefix</label>
                <input
                  type="text"
                  className="form-control d-inline"
                  disabled
                  key={`${state.collection?.code}_${state.artwork_name?.code}_`}
                  value={
                    state.collection?.code &&
                    state.artwork_name?.code &&
                    `${state.collection?.code}_${state.artwork_name?.code}_`
                  }
                />
              </div>
              <div className="col-md-2 form-group align-self-center mb-0">
                <label className="form-label">Meta Artwork Number*</label>
                <input
                  type="number"
                  onChange={(e) => {
                    dispatch(
                      MasterArtworkNumberActions.setCode(
                        `${state.collection?.code}_${state.artwork_name?.code}_${e.target.value}`,
                      ),
                    );
                  }}
                  onFocus={() => {
                    setNextArtworkNumber();
                    getMissingConditionsMessage();
                  }}
                  min={1}
                  value={state.code?.split("_").reverse()[0]}
                  className="form-control d-inline"
                  disabled={state.isReadOnly}
                />
              </div>
              <div className="col-md-2 form-group align-self-center mb-0">
                <label className="form-label">Meta Artwork Image</label>
                <button
                  onClick={() => {
                    !(state.file_name || state.isReadOnly) &&
                      setShowUploadModal(true);
                    state.file_name && setShowPictureModal(true);
                  }}
                  disabled={state.isReadOnly && !state.file_name}
                  type="button"
                  className="icon-button upload-button form-control"
                >
                  {state.file_name ? (
                    <img
                      src={
                        process.env.PUBLIC_URL + "/assets/img/uploadSuccess.png"
                      }
                      alt="upload success"
                      className=""
                      width={30}
                    ></img>
                  ) : (
                    <img
                      src={
                        process.env.PUBLIC_URL + "/assets/img/uploadPending.png"
                      }
                      alt="upload pending"
                      className=""
                      width={26}
                    ></img>
                  )}
                </button>
              </div>
            </div>
          </ExpandableSection>

          {historyRecords.length > 0 && (
            <ArtworkRequestHistoryTable historyRecords={historyRecords} />
          )}

          <ReactModal
            isOpen={showUploadModal}
            className="custom-modal barcode-img-modal"
            overlayClassName="custom-overlay"
          >
            <ArtworkUploadModal
              setArtworkImage={(e: any) => {
                dispatch(MasterArtworkNumberActions.setFileName(e));
              }}
              uploadedImage={state.file_name}
              handleCloseModal={() => setShowUploadModal(false)}
            />
          </ReactModal>

          <ReactModal
            isOpen={showPictureModal}
            className="custom-modal mold-comps"
            overlayClassName="custom-overlay"
          >
            <ArtworkPictureModal
              handleCloseModal={closeModal}
              product={{
                imageName: `Artwork Number ${state.code}`,
                image_file: `data:image/jpeg;base64,${artworkNumberImg}`,
              }}
            />

            {!state.isReadOnly && (
              <div className="row justify-content-center mb-3">
                <button
                  className="btn btn-danger col-2 mx-auto mt-1 mb-1 justify-self-center"
                  onClick={() => {
                    dispatch(MasterArtworkNumberActions.setFileName(""));

                    closeModal();
                  }}
                >
                  Remove image
                </button>
              </div>
            )}
          </ReactModal>

          {!state.isValid && !state.isReadOnly && (
            <>
              <Banner
                classname={"mt-3"}
                message={getMissingConditionsMessage()}
                type="danger"
              />
            </>
          )}

          <ArtworkNumberActionButtonsGroup
            //@ts-ignore
            isReadOnlyRequest={state.isReadOnly}
            isEditing={Predicates.isNotNullAndNotUndefinedAndNotEmpty(
              isEditing,
            )}
            isSaveOrSubmitInProgress={isSaveOrSubmitInProgress}
            username={username ?? ""}
            hasApproverPermission={hasApproverPermission}
            hasCreatorPermission={hasCreatorPermission}
            setIsSaveOrSubmitInProgress={setIsSaveOrSubmitInProgress}
          />
        </div>
      )}
    </>
  );
};
